/**
 * Poly popup event system for communication between independently hydrated blocks.
 */

export const POPUP_EVENT_NAME = 'poly:open-popup';

export interface PopupEventDetail {
  popupId: string;
}

/** Normalise a Popup ID consistently at the trigger and destination. */
export const normalizePopupId = (popupId?: string): string =>
  (popupId ?? '')
    .trim()
    .replace(/^#+/, '')
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9-]/g, '');

/**
 * Dispatch a Popup request from a CTA. Requests originating inside a Popup are ignored so one
 * Popup can never open another.
 */
export function dispatchPopupEvent(element: HTMLElement, popupId: string): void {
  const normalizedId = normalizePopupId(popupId);
  if (!normalizedId || element.closest('[data-poly-block="Popup"], .poly-popup__panel')) return;

  element.dispatchEvent(
    new CustomEvent<PopupEventDetail>(POPUP_EVENT_NAME, {
      bubbles: true,
      detail: { popupId: normalizedId },
    }),
  );
}

/** Listen for Popup requests and provide the control that initiated each request. */
export function onPopupEvent(
  handler: (popupId: string, trigger: HTMLElement | null) => void,
): () => void {
  const listener = (event: Event) => {
    const { popupId } = (event as CustomEvent<PopupEventDetail>).detail;
    handler(popupId, event.target instanceof HTMLElement ? event.target : null);
  };
  document.addEventListener(POPUP_EVENT_NAME, listener);
  return () => document.removeEventListener(POPUP_EVENT_NAME, listener);
}
